/**
 * sortStateParser
 * Reads the saved sort criteria the workbook was authored with, so the
 * viewer can re-apply it on load.  Excel stores this as:
 *
 *   <autoFilter ref="A1:D100">
 *     <sortState ref="A2:D100">
 *       <sortCondition ref="B2:B100" descending="1"/>
 *     </sortState>
 *   </autoFilter>
 *
 * We read worksheet XML straight from the zip (ExcelJS does not surface
 * sortState reliably) and return, per sheet, the first sort condition as
 * { col: <0-based>, descending: <bool> }.  Worksheets are matched to
 * sheet names positionally (sheet1.xml → first sheet), which holds for
 * the vast majority of Excel-written files.
 */

function colFromRef(ref) {
  const first = String(ref).split(":")[0];
  const m = first.match(/^\$?([A-Za-z]+)\$?\d+$/);
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i += 1) col = col * 26 + (letters.charCodeAt(i) - 64);
  return col - 1;
}

function parseSortStateFromXml(xml) {
  if (!xml) return null;
  const block = xml.match(/<sortState\b[^>]*>([\s\S]*?)<\/sortState>/i);
  if (!block) return null;
  const cond = block[1].match(/<sortCondition\b[^>]*\/?>/i);
  if (!cond) return null;
  const refM = cond[0].match(/\bref="([^"]+)"/i);
  if (!refM) return null;
  const col = colFromRef(refM[1]);
  if (col == null) return null;
  const descM = cond[0].match(/\bdescending="([^"]+)"/i);
  const descending = descM ? (descM[1] === "1" || /true/i.test(descM[1])) : false;
  return { col, descending };
}

/**
 * Parse the worksheet-level autoFilter range, e.g. <autoFilter ref="A1:D100">,
 * and return the 0-based column span { c1, c2 } the filter covers.  This is
 * what tells the viewer which columns Excel would show a filter dropdown on.
 */
function parseAutoFilterColsFromXml(xml) {
  if (!xml) return null;
  const m = xml.match(/<autoFilter\b[^>]*\bref="([^"]+)"/i);
  if (!m) return null;
  const parts = m[1].split(":");
  const c1 = colFromRef(parts[0]);
  const c2 = colFromRef(parts[1] || parts[0]);
  if (c1 == null || c2 == null) return null;
  return { c1: Math.min(c1, c2), c2: Math.max(c1, c2) };
}

// Iterate the worksheet XML files once, applying `pick` to each and keying
// the result by the positional sheet name.
async function perSheetFromZip(zip, sheetNames, pick) {
  const result = {};
  if (!zip || !Array.isArray(sheetNames)) return result;
  try {
    const files = Object.keys(zip.files)
      .filter((f) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(f))
      .sort((a, b) => {
        const na = parseInt(a.match(/sheet(\d+)\.xml/i)?.[1] || "0", 10);
        const nb = parseInt(b.match(/sheet(\d+)\.xml/i)?.[1] || "0", 10);
        return na - nb;
      });
    for (let i = 0; i < files.length; i += 1) {
      const name = sheetNames[i];
      if (!name) continue;
      // eslint-disable-next-line no-await-in-loop
      const xml = await zip.file(files[i])?.async("string");
      const v = pick(xml);
      if (v) result[name] = v;
    }
  } catch (_) {
    /* best-effort; never block rendering */
  }
  return result;
}

/** Minimal XML entity unescape for attribute values read via regex. */
function xmlUnescape(s) {
  return String(s)
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

/**
 * Parse the ACTIVE filter criteria saved inside <autoFilter> —
 * Excel stores an applied value filter as:
 *
 *   <autoFilter ref="A1:D100">
 *     <filterColumn colId="2">
 *       <filters blank="1"><filter val="North"/><filter val="South"/></filters>
 *     </filterColumn>
 *   </autoFilter>
 *
 * Returns { [absoluteColIndex]: [allowedValue, …] } with "(Blank)" standing
 * in for blanks (the viewer's FilterMenu convention), or null when the file
 * has no applied criteria.  colId is RELATIVE to the autoFilter range.
 */
function parseAutoFilterCriteriaFromXml(xml) {
  if (!xml) return null;
  const afBlock = xml.match(/<autoFilter\b[^>]*\bref="([^"]+)"[^>]*>([\s\S]*?)<\/autoFilter>/i);
  if (!afBlock) return null;
  const baseCol = colFromRef(afBlock[1].split(":")[0]);
  if (baseCol == null) return null;

  const criteria = {};
  const fcRe = /<filterColumn\b([^>]*)>([\s\S]*?)<\/filterColumn>/gi;
  let fc;
  while ((fc = fcRe.exec(afBlock[2])) !== null) {
    const colIdM = fc[1].match(/\bcolId="(\d+)"/i);
    if (!colIdM) continue;
    const absCol = baseCol + parseInt(colIdM[1], 10);

    const filtersBlock = fc[2].match(/<filters\b([^>]*)>([\s\S]*?)<\/filters>/i);
    if (!filtersBlock) continue; // custom/date/color filters — not supported here
    const allowed = [];
    if (/\bblank="(1|true)"/i.test(filtersBlock[1])) allowed.push("(Blank)");
    const valRe = /<filter\b[^>]*\bval="([^"]*)"[^>]*\/?>/gi;
    let v;
    while ((v = valRe.exec(filtersBlock[2])) !== null) allowed.push(xmlUnescape(v[1]));
    if (allowed.length > 0) criteria[absCol] = allowed;
  }
  return Object.keys(criteria).length > 0 ? criteria : null;
}

export async function extractSortStatesFromZip(zip, sheetNames) {
  return perSheetFromZip(zip, sheetNames, parseSortStateFromXml);
}

export async function extractAutoFilterColsFromZip(zip, sheetNames) {
  return perSheetFromZip(zip, sheetNames, parseAutoFilterColsFromXml);
}

export async function extractAutoFilterCriteriaFromZip(zip, sheetNames) {
  return perSheetFromZip(zip, sheetNames, parseAutoFilterCriteriaFromXml);
}
